import { useEffect, useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
const TITLE = "Calculatrice d'age hegirien (calendrier islamique)";
const DESCRIPTION =
  "Calculez votre age hegirien depuis une date gregorienne ou hegirienne. Obtenez un resultat detaille et une conversion claire entre les deux calendriers.";
const KEYWORDS =
  "calculatrice age hegirien, age islamique, calendrier hegirien, conversion gregorien hegirien, date hegirienne";
const OG_URL = "https://do-calculate.com/calculator/fr/agehijri/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice d'Âge Hégirien",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Toutes les Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice d'Âge Hégirien",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Qu'est-ce que l'âge hégirien?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "L'âge hégirien est la méthode utilisée pour mesurer l'âge d'une personne en fonction de sa date de naissance selon le calendrier hégirien/islamique.",
        },
      },
      {
        "@type": "Question",
        name: "Comment puis-je calculer mon âge hégirien?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Vous pouvez calculer votre âge hégirien en saisissant votre date de naissance dans la calculatrice, qui calculera automatiquement votre âge en années, mois, jours et heures avec précision.",
        },
      },
      {
        "@type": "Question",
        name: "Comment utiliser cette calculatrice d'âge hégirien?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Entrez votre date de naissance en format grégorien ou hégirien, et la calculatrice affichera votre âge exact selon le calendrier hégirien.",
        },
      },
    ],
  },
];
const GREGORIAN_MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];
const HIJRI_MONTHS = [
  "Muharram",
  "Safar",
  "Rabi' al-awwal",
  "Rabi' al-Thani",
  "Jumada al-awwal",
  "Jumada al-Thani",
  "Rajab",
  "Sha'ban",
  "Ramadan",
  "Shawwal",
  "Dhu al-Qi'dah",
  "Dhu al-Hijjah",
];
const SEASONS = [
  {
    name: "Hiver",
    start: { month: 12, day: 21 },
    end: { month: 3, day: 20 },
  },
  { name: "Printemps", start: { month: 3, day: 21 }, end: { month: 6, day: 20 } },
  { name: "Été", start: { month: 6, day: 21 }, end: { month: 9, day: 22 } },
  {
    name: "Automne",
    start: { month: 9, day: 23 },
    end: { month: 12, day: 20 },
  },
];

// Helper function to convert Hijri to Gregorian
const hijriToGregorian = (hijriDay, hijriMonth, hijriYear) => {
  const adjustment = Math.floor(hijriYear * 0.97);
  const gregorianYear = hijriYear + 622 - adjustment;
  const monthDays = [30, 29, 30, 29, 30, 29, 30, 29, 30, 29, 30, 29];
  let totalDays = 0;
  for (let i = 0; i < hijriMonth - 1; i++) {
    totalDays += monthDays[i];
  }
  totalDays += hijriDay - 1;
  const gregorianDays = Math.floor(totalDays * 1.0307);
  const date = new Date(gregorianYear, 0, 1);
  date.setDate(date.getDate() + gregorianDays);
  return date;
};

const getCurrentHijriYear = () => {
  const currentDate = new Date();
  const currentGregorianYear = currentDate.getFullYear();
  return Math.floor((currentGregorianYear - 622) * 1.03);
};

function AgeCalculatorHijri() {
  const [convertedValue, setConvertedValue] = useState("hijri");
  const [dateOfBirthGregorian, setDateOfBirthGregorian] = useState({
    day: "",
    month: "",
    year: ""
  });
  const [dateOfBirthHijri, setDateOfBirthHijri] = useState({
    day: "",
    month: "",
    year: ""
  });
  const [ageResult, setAgeResult] = useState(null);
  const [error, setError] = useState(null);
  const [hijriYears, setHijriYears] = useState([]);

  useEffect(() => {
    const currentHijriYear = getCurrentHijriYear();
    const years = [];
    for (let year = 1300; year <= currentHijriYear + 10; year++) {
      years.push(year);
    }
    setHijriYears(years);
  }, []);

  const handleConversionValueChange = (newConversionType) => {
    setConvertedValue(newConversionType);
    setError(null);
    setDateOfBirthGregorian({ day: "", month: "", year: "" });
    setDateOfBirthHijri({ day: "", month: "", year: "" });
    setAgeResult(null);
  };

  const calculateAge = (birthDate, isHijri = false) => {
    const today = new Date();
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime()) || birth > today) {
      setError("Veuillez entrer une date de naissance valide");
      setAgeResult(null);
      return;
    }

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    const totalDays = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
    const totalHours = Math.floor((today - birth) / (1000 * 60 * 60));

    setAgeResult({
      years, months, days,
      totalDays,
      totalHours,
      birthDateFormatted: birth.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    });
    setError(null);
  };

  const handleCalculateGregorian = () => {
    if (!dateOfBirthGregorian.day || !dateOfBirthGregorian.month || !dateOfBirthGregorian.year) {
      setError("Veuillez entrer une date complète");
      return;
    }
    const birth = new Date(dateOfBirthGregorian.year, dateOfBirthGregorian.month - 1, dateOfBirthGregorian.day);
    calculateAge(birth, false);
  };

  const handleCalculateHijri = () => {
    if (!dateOfBirthHijri.day || !dateOfBirthHijri.month || !dateOfBirthHijri.year) {
      setError("Veuillez entrer une date hégirien complète");
      return;
    }
    const gregorianDate = hijriToGregorian(parseInt(dateOfBirthHijri.day), parseInt(dateOfBirthHijri.month), parseInt(dateOfBirthHijri.year));
    calculateAge(gregorianDate, true);
  };

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center p-3">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light p-3">
            <p>
              Cette calculatrice vous permet de calculer votre âge selon le calendrier hégirien/islamique. Vous pouvez entrer votre date de naissance en format grégorien ou hégirien, et la calculatrice affichera votre âge exact.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle p-3 bg-light rounded">
              <div className="mb-4">
                <label><strong>Sélectionnez le type de calcul:</strong></label>
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn ${convertedValue === "gregorian" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => handleConversionValueChange("gregorian")}
                  >
                    À partir de Grégorien
                  </button>
                  <button
                    type="button"
                    className={`btn ${convertedValue === "hijri" ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => handleConversionValueChange("hijri")}
                  >
                    À partir d'Hégirien
                  </button>
                </div>
              </div>

              {convertedValue === "gregorian" && (
                <div>
                  <div className="p-2 mb-3 bg-success text-white">
                    <strong>Entrez votre date de naissance (Grégorien)</strong>
                  </div>
                  <div className="mb-3">
                    <label><strong>Jour:</strong></label>
                    <select
                      className="form-control"
                      value={dateOfBirthGregorian.day}
                      onChange={(e) => setDateOfBirthGregorian({ ...dateOfBirthGregorian, day: e.target.value })}
                    >
                      <option value="">Jour</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label><strong>Mois:</strong></label>
                    <select
                      className="form-control"
                      value={dateOfBirthGregorian.month}
                      onChange={(e) => setDateOfBirthGregorian({ ...dateOfBirthGregorian, month: e.target.value })}
                    >
                      <option value="">Mois</option>
                      {GREGORIAN_MONTHS.map((m, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label><strong>Année:</strong></label>
                    <select
                      className="form-control"
                      value={dateOfBirthGregorian.year}
                      onChange={(e) => setDateOfBirthGregorian({ ...dateOfBirthGregorian, year: e.target.value })}
                    >
                      <option value="">Année</option>
                      {Array.from({ length: 150 }, (_, i) => new Date().getFullYear() - i).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <button className="btn btn-success" onClick={handleCalculateGregorian}>
                    Calculer l'Âge Hégirien
                  </button>
                </div>
              )}

              {convertedValue === "hijri" && (
                <div>
                  <div className="p-2 mb-3 bg-success text-white">
                    <strong>Entrez votre date de naissance (Hégirien)</strong>
                  </div>
                  <div className="mb-3">
                    <label><strong>Jour:</strong></label>
                    <select
                      className="form-control"
                      value={dateOfBirthHijri.day}
                      onChange={(e) => setDateOfBirthHijri({ ...dateOfBirthHijri, day: e.target.value })}
                    >
                      <option value="">Jour</option>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label><strong>Mois:</strong></label>
                    <select
                      className="form-control"
                      value={dateOfBirthHijri.month}
                      onChange={(e) => setDateOfBirthHijri({ ...dateOfBirthHijri, month: e.target.value })}
                    >
                      <option value="">Mois</option>
                      {HIJRI_MONTHS.map((m, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label><strong>Année:</strong></label>
                    <select
                      className="form-control"
                      value={dateOfBirthHijri.year}
                      onChange={(e) => setDateOfBirthHijri({ ...dateOfBirthHijri, year: e.target.value })}
                    >
                      <option value="">Année</option>
                      {hijriYears.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <button className="btn btn-success" onClick={handleCalculateHijri}>
                    Calculer l'Âge
                  </button>
                </div>
              )}

              {error && (
                <div className="alert alert-danger mt-3">{error}</div>
              )}

              {ageResult && (
                <div className="alert alert-success mt-4">
                  <h3>Votre Âge:</h3>
                  <div className="mb-3">
                    <strong style={{ fontSize: "24px", color: "#28a745" }}>
                      {ageResult.years} ans, {ageResult.months} mois et {ageResult.days} jours
                    </strong>
                  </div>
                  <div className="mt-3">
                    <p><strong>Détails:</strong></p>
                    <ul>
                      <li>Jours totaux: {ageResult.totalDays}</li>
                      <li>Heures totales: {ageResult.totalHours}</li>
                      <li>Date de naissance: {ageResult.birthDateFormatted}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
          <br />
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
          <RelatedLinks />
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Calcul d'age selon le calendrier hegirien</h3></div>
              <div className="info-section-body">
                <p>Le calendrier hegirien est lunaire et comporte 12 mois, pour environ 354 jours par an. Cette difference avec l'annee solaire explique pourquoi l'age hegirien est legerement plus eleve que l'age gregorien a date egale.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Difference entre calendrier hegirien et gregorien</h3></div>
              <div className="info-section-body">
                <p>Le calendrier gregorien est solaire (365 ou 366 jours), tandis que le calendrier hegirien est lunaire (environ 354 jours). Le decalage annuel est proche de 11 jours, ce qui deplace progressivement les dates religieuses dans l'annee civile.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">L'âge hégirien : guide complet</div>
              <div className="info-section-body">
                <p>Le <strong>calendrier hégirien</strong> (ou islamique) est un calendrier lunaire qui compte 12 mois de 29 ou 30 jours, soit environ 354 jours par an. Il date de la migration du Prophète Mohammed de La Mecque à Médine en 622 après J.-C. (année 1 de l'hégire).</p>
                <p><strong>Calcul de l'âge hégirien :</strong></p>
                <ol>
                  <li>Convertir la date de naissance en date hégirienne</li>
                  <li>Soustraire cette date de la date hégirienne actuelle</li>
                  <li>Le résultat en années hégiriennes est légèrement plus élevé que l'âge grégorien (écart d'environ 3 ans sur 100)</li>
                </ol>
                <p><strong>Exemple :</strong> une personne née le 1er janvier 1990 a environ 35 ans grégoriens mais son âge hégirien est d'environ 36 ans, car les années hégiriennes sont plus courtes.</p>
                <p><strong>Utilisations de l'âge hégirien :</strong></p>
                <ul>
                  <li>Calcul de la Zakat (aumone obligatoire, due à partir de la majorité islamique)</li>
                  <li>Détermination des dates de fêtes religieuses (Ramadan, Aid)</li>
                  <li>Documents officiels dans certains pays arabes</li>
                  <li>Connaissance de son propre âge selon la tradition islamique</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Questions fréquentes</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><strong>Qu'est-ce que l'âge hégirien ?</strong></h3>
                <p>L'âge hégirien est l'âge calculé selon le calendrier islamique lunaire. Il est généralement légèrement plus élevé que l'âge grégorien car l'année hégirienne est plus courte d'environ 11 jours.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Comment calcule-t-on l'âge hégirien ?</strong></h3>
                <p>Entrez votre date de naissance (grégorienne ou hégirienne). La calculatrice convertit automatiquement et calcule l'âge selon les deux systèmes calendaires.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Pourquoi l'âge hégirien est-il supérieur à l'âge grégorien ?</strong></h3>
                <p>Parce que l'année hégirienne (354 jours) est plus courte que l'année grégorienne (365 jours). Sur une vie de 60 ans grégoriens, on a vécu environ 62 années hégiriennes.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>La calculatrice fonctionne-t-elle avec le calendrier Oum Al-Qura ?</strong></h3>
                <p>Oui, les calculs utilisent les tables astronomiques standard du calendrier hégirien, compatibles avec le calendrier Oum Al-Qura utilisé en Arabie Saoudite et dans de nombreux pays arabes.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgeCalculatorHijri;
